import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "axiom_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

export function AdminLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      setToken(data.token);
      nav("/admin");
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Connexion impossible");
    } finally { setLoading(false); }
  };

  if (getToken()) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen paper-bg flex items-center justify-center p-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-[var(--ax-line)] p-10">
        <Link to="/" className="ax-monogram block mb-8">AXIOM<span>.</span></Link>
        <div className="ax-eyebrow mb-2">— Espace administrateur</div>
        <h1 className="font-display text-4xl mb-8">Connexion</h1>
        <div className="space-y-6">
          <div>
            <label className="ax-label">Email</label>
            <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="ax-input" data-testid="login-email" />
          </div>
          <div>
            <label className="ax-label">Mot de passe</label>
            <input required type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="ax-input" data-testid="login-password" />
          </div>
          {err && <div className="text-red-600 text-sm font-sans-ax" data-testid="login-error">{err}</div>}
          <button type="submit" disabled={loading} className="btn-gold w-full justify-center" data-testid="login-submit">
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setMsg("");
    if (pw1.length < 8) { setErr("Le nouveau mot de passe doit faire au moins 8 caractères"); return; }
    if (pw1 !== pw2) { setErr("Les deux mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cur, new_password: pw1 }, { headers: authHeaders() });
      setMsg("Mot de passe modifié avec succès");
      setCur(""); setPw1(""); setPw2("");
      setTimeout(onClose, 1200);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="pwd-modal" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-md p-8 border border-[var(--ax-line)]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-2xl">Modifier mon mot de passe</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-black text-xl" data-testid="pwd-close">×</button>
        </div>
        <div className="space-y-5">
          <div><label className="ax-label">Mot de passe actuel</label><input required type="password" value={cur} onChange={(e)=>setCur(e.target.value)} className="ax-input" data-testid="pwd-current"/></div>
          <div><label className="ax-label">Nouveau mot de passe (min 8)</label><input required type="password" value={pw1} onChange={(e)=>setPw1(e.target.value)} className="ax-input" data-testid="pwd-new"/></div>
          <div><label className="ax-label">Confirmer</label><input required type="password" value={pw2} onChange={(e)=>setPw2(e.target.value)} className="ax-input" data-testid="pwd-confirm"/></div>
          {err && <div className="text-red-600 text-sm" data-testid="pwd-error">{err}</div>}
          {msg && <div className="text-green-700 text-sm" data-testid="pwd-success">{msg}</div>}
          <button type="submit" disabled={busy} className="btn-gold w-full justify-center" data-testid="pwd-submit">{busy?"…":"Mettre à jour"}</button>
        </div>
      </form>
    </div>
  );
}

function ArticleForm({ initial, onSave, onCancel }) {
  const [a, setA] = useState(initial || { title:"", slug:"", category:"", excerpt:"", content:"", cover_image:"", read_time:"5 min" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"").slice(0,80);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: authHeaders() });
    // Cloudinary returns absolute https URL; local fallback returns /api/uploads/...
    const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    setA({ ...a, cover_image: url });
  };

  const save = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const payload = { ...a, slug: a.slug || slugify(a.title) };
      if (initial?.id) await axios.put(`${API}/admin/articles/${initial.id}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/articles`, payload, { headers: authHeaders() });
      onSave();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Erreur de sauvegarde");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-[var(--ax-line)] p-8 space-y-5" data-testid="article-form">
      <h2 className="font-display text-3xl">{initial?.id ? "Modifier l'article" : "Nouvel article"}</h2>
      <div className="grid md:grid-cols-2 gap-5">
        <div><label className="ax-label">Titre *</label><input required value={a.title} onChange={(e)=>setA({...a,title:e.target.value,slug:a.slug||slugify(e.target.value)})} className="ax-input" data-testid="art-title"/></div>
        <div><label className="ax-label">Slug URL *</label><input required value={a.slug} onChange={(e)=>setA({...a,slug:slugify(e.target.value)})} className="ax-input" data-testid="art-slug"/></div>
        <div><label className="ax-label">Catégorie *</label><input required value={a.category} onChange={(e)=>setA({...a,category:e.target.value})} className="ax-input" data-testid="art-category"/></div>
        <div><label className="ax-label">Temps de lecture</label><input value={a.read_time} onChange={(e)=>setA({...a,read_time:e.target.value})} className="ax-input" data-testid="art-readtime"/></div>
      </div>
      <div><label className="ax-label">Extrait (résumé court) *</label><textarea required value={a.excerpt} onChange={(e)=>setA({...a,excerpt:e.target.value})} className="ax-textarea" rows={2} data-testid="art-excerpt"/></div>
      <div>
        <label className="ax-label">Image de couverture (optionnel)</label>
        <input type="file" accept="image/*" onChange={upload} className="font-sans-ax text-sm" data-testid="art-upload"/>
        {a.cover_image && <img src={a.cover_image} alt="" className="mt-2 h-28 object-cover border border-[var(--ax-line)]"/>}
      </div>
      <div>
        <label className="ax-label">Contenu * (Markdown léger : **gras** pour les titres)</label>
        <textarea required value={a.content} onChange={(e)=>setA({...a,content:e.target.value})} className="ax-textarea font-mono-ax text-sm" rows={14} data-testid="art-content"/>
      </div>
      {err && <div className="text-red-600 text-sm">{err}</div>}
      <div className="flex gap-3">
        <button type="submit" disabled={busy} className="btn-gold" data-testid="art-save">{busy?"Enregistrement…":"Enregistrer"}</button>
        <button type="button" onClick={onCancel} className="btn-outline-ink" data-testid="art-cancel">Annuler</button>
      </div>
    </form>
  );
}

export function AdminDashboard() {
  const [articles, setArticles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("articles");
  const [editing, setEditing] = useState(null); // null | "new" | article
  const [showPwd, setShowPwd] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [aRes, mRes] = await Promise.all([
        axios.get(`${API}/articles`),
        axios.get(`${API}/admin/messages`, { headers: authHeaders() }),
      ]);
      setArticles(aRes.data); setMessages(mRes.data);
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); nav("/admin/login"); }
    }
  };
  useEffect(() => { if (!getToken()) nav("/admin/login"); else load(); }, []);

  const delArt = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await axios.delete(`${API}/admin/articles/${id}`, { headers: authHeaders() });
    load();
  };
  const delMsg = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeaders() });
    load();
  };
  const logout = () => { clearToken(); nav("/admin/login"); };

  return (
    <div className="min-h-screen paper-bg" data-testid="admin-dashboard">
      <header className="bg-[var(--ax-black)] text-[var(--ax-cream)] px-6 md:px-12 py-5 flex justify-between items-center">
        <Link to="/" className="ax-monogram !text-[var(--ax-cream)]">AXIOM<span className="!text-[var(--ax-gold)]">.</span> <span className="text-xs ml-2 opacity-60">admin</span></Link>
        <div className="flex gap-6 items-center font-sans-ax text-sm">
          <button onClick={()=>{setTab("articles");setEditing(null);}} className={tab==="articles"?"text-[var(--ax-gold)]":"text-neutral-300"} data-testid="tab-articles">Articles ({articles.length})</button>
          <button onClick={()=>{setTab("messages");setEditing(null);}} className={tab==="messages"?"text-[var(--ax-gold)]":"text-neutral-300"} data-testid="tab-messages">Messages ({messages.length})</button>
          <Link to="/" className="text-neutral-300 hover:text-[var(--ax-gold)]">Voir le site →</Link>
          <button onClick={()=>setShowPwd(true)} className="text-neutral-300 hover:text-[var(--ax-gold)]" data-testid="open-pwd-btn">Mot de passe</button>
          <button onClick={logout} className="text-neutral-300 hover:text-[var(--ax-gold)]" data-testid="logout-btn">Déconnexion</button>
        </div>
      </header>
      {showPwd && <ChangePasswordModal onClose={()=>setShowPwd(false)} />}

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {editing ? (
          <ArticleForm initial={editing==="new"?null:editing} onSave={()=>{setEditing(null);load();}} onCancel={()=>setEditing(null)} />
        ) : tab === "articles" ? (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-display text-4xl">Articles du blog</h1>
              <button onClick={()=>setEditing("new")} className="btn-gold" data-testid="new-article-btn">+ Nouvel article</button>
            </div>
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.id} className="bg-white border border-[var(--ax-line)] p-5 flex justify-between items-center" data-testid={`admin-article-${a.slug}`}>
                  <div className="flex-1">
                    <div className="text-xs font-mono-ax tracking-widest text-[var(--ax-gold-deep)]">{a.category.toUpperCase()} · {a.date}</div>
                    <div className="font-display text-xl mt-1">{a.title}</div>
                    <div className="text-sm text-neutral-500 font-sans-ax mt-1">/blog/{a.slug}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={()=>setEditing(a)} className="btn-outline-ink !py-2 !px-4 !text-xs" data-testid={`edit-${a.slug}`}>Modifier</button>
                    <button onClick={()=>delArt(a.id)} className="!py-2 !px-4 !text-xs border border-red-300 text-red-600 hover:bg-red-50 font-sans-ax uppercase tracking-widest" data-testid={`del-${a.slug}`}>Supprimer</button>
                  </div>
                </div>
              ))}
              {articles.length === 0 && <div className="text-neutral-500 text-center py-12">Aucun article. Créez le premier !</div>}
            </div>
          </div>
        ) : (
          <div>
            <h1 className="font-display text-4xl mb-8">Messages reçus</h1>
            <div className="space-y-3">
              {messages.map((m) => (
                <details key={m.id} className="bg-white border border-[var(--ax-line)] p-5" data-testid={`msg-${m.id}`}>
                  <summary className="cursor-pointer flex justify-between items-center">
                    <div>
                      <div className="font-display text-lg">{m.name} <span className="text-neutral-500 text-sm">— {m.email}</span></div>
                      <div className="text-sm text-neutral-500 font-sans-ax">{m.subject || "Sans sujet"} · {new Date(m.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                    <button onClick={(e)=>{e.preventDefault();delMsg(m.id);}} className="text-red-600 hover:underline text-xs font-sans-ax">Supprimer</button>
                  </summary>
                  <div className="mt-4 pt-4 border-t border-[var(--ax-line)] text-sm font-sans-ax space-y-2">
                    {m.phone && <div><strong>Tél :</strong> {m.phone}</div>}
                    <div className="whitespace-pre-wrap text-neutral-700">{m.message}</div>
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject||"")}`} className="inline-block mt-3 text-[var(--ax-gold-deep)] underline">Répondre par email →</a>
                  </div>
                </details>
              ))}
              {messages.length === 0 && <div className="text-neutral-500 text-center py-12">Aucun message reçu pour le moment.</div>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
